// @ts-check
/**
 * Admin WhatsApp outreach: templates, campaigns, the inbox and automated triggers. One method per
 * backend endpoint (backend/routes/admin/whatsapp_*.py), all through the shared apiClient, so errors
 * come back as Error objects with `status` (and `code` / `detail` when the server sent them).
 */

import api from '../utils/apiClient';

const BASE = '/api/admin/whatsapp';

/**
 * @typedef {Object} WhatsAppStatus
 * @property {boolean} configured credentials are present on this environment
 * @property {boolean} send_enabled WHATSAPP_SEND_ENABLED is on, so messages are really sent
 *
 * @typedef {Object} WhatsAppTemplate
 * @property {string} id
 * @property {string} name
 * @property {string} language
 * @property {string | null} category
 * @property {string | null} status approved, pending, rejected ...
 * @property {string} body raw text with {{n}} placeholders
 * @property {string | null} header_text
 * @property {string | null} footer_text
 * @property {Array<Record<string, any>>} buttons
 * @property {number} variable_count
 * @property {string} preview_body body with placeholders shown as [1], [2]
 * @property {string | null} meta_template_id
 * @property {string | null} synced_at
 *
 * @typedef {Object} TemplateSyncResult
 * @property {number} created
 * @property {number} updated
 * @property {number} skipped
 * @property {number} total
 *
 * @typedef {Object} AudienceInput
 * @property {string} source walk_in_lead, customer, atelier_contact or csv
 * @property {Record<string, any>} [filters] walk_in_lead needs filters.event_ids
 * @property {string[]} [exclude_source_ids]
 *
 * @typedef {Object} AudiencePreview
 * @property {number} sendable_count
 * @property {Array<{name: string | null, phone: string | null, reason: string}>} skipped
 * @property {Array<{name: string | null, phone: string, source_id: string | null}>} recipients
 *
 * @typedef {Object} CampaignInput
 * @property {string} name
 * @property {string} template_id
 * @property {Record<string, string>} variable_map placeholder index -> value source (first_name ...)
 * @property {string} source
 * @property {Record<string, any>} [filters]
 * @property {string[]} [exclude_source_ids]
 *
 * @typedef {Object} Campaign
 * @property {string} id
 * @property {string} name
 * @property {string} template_id
 * @property {string | null} template_name
 * @property {Record<string, string>} variable_map
 * @property {Record<string, any>} audience
 * @property {string} status draft, scheduled, sending, completed or cancelled
 * @property {Record<string, number>} counts number of messages per message status
 * @property {number} total
 * @property {string | null} created_at
 * @property {string | null} test_sent_at
 * @property {string | null} scheduled_at
 * @property {string | null} started_at
 * @property {string | null} finished_at
 *
 * @typedef {Object} CampaignRecipient
 * @property {string} id
 * @property {string | null} name
 * @property {string} phone
 * @property {string} source
 * @property {string | null} source_id
 * @property {string} status
 * @property {string} display_status like status, or "not_sent" for rows left queued by a cancel
 * @property {string | null} skip_reason
 * @property {string | null} error
 * @property {string | null} sent_at
 * @property {string | null} delivered_at
 * @property {string | null} read_at
 *
 * @typedef {Object} RecipientsPage
 * @property {CampaignRecipient[]} items
 * @property {number} total rows matching the status filter
 * @property {number} page 1-based
 * @property {number} limit
 * @property {number} pages
 *
 * @typedef {Campaign & {template: {name: string, language: string, body: string} | null, recipients: RecipientsPage}} CampaignDetail
 *
 * @typedef {Object} RetryResult
 * @property {string} status always "sending"
 * @property {number} requeued
 * @property {number} uncertain_left failed rows not retried because they may already be delivered
 *
 * @typedef {Object} Conversation
 * @property {string} phone E.164
 * @property {string | null} name
 * @property {string} last_message_text
 * @property {string | null} last_message_at
 * @property {'in' | 'out'} last_direction
 * @property {number} unread_count
 * @property {boolean} suppressed
 * @property {boolean} window_open
 * @property {string | null} last_inbound_at
 *
 * @typedef {Object} ThreadMessage
 * @property {string} id
 * @property {'in' | 'out'} direction
 * @property {string} text
 * @property {string} at
 * @property {string} [status]
 * @property {string} [template_name]
 * @property {string} [source]
 * @property {string} [message_type] inbound only
 * @property {boolean} [media_pending] inbound only - media exists on Meta's side but hasn't been fetched yet; call fetchInboundMedia(id)
 * @property {string | null} [media_url]
 * @property {string | null} [media_mime_type]
 * @property {string | null} [media_filename]
 *
 * @typedef {Object} ConversationDetail
 * @property {{name: string | null, source: string | null, notes: string | null, lead: any, customer: any, atelier: any}} contact
 * @property {boolean} suppressed
 * @property {boolean} window_open
 * @property {string | null} last_inbound_at
 * @property {ThreadMessage[]} messages oldest first
 *
 * @typedef {Object} Trigger
 * @property {string} key order_confirmed, order_shipped, order_delivered or lead_captured
 * @property {string} label
 * @property {string} description
 * @property {string | null} template_id
 * @property {string | null} template_name
 * @property {Record<string, string>} variable_map
 * @property {boolean} enabled
 * @property {string[]} placeholders value sources this trigger can fill
 */

/**
 * Query string from the entries that have a value; "" when none do.
 * @param {Record<string, string | number | null | undefined>} params
 */
const query = (params) => {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') search.set(key, String(value));
  });
  const text = search.toString();
  return text ? `?${text}` : '';
};

/** Phone numbers are path parameters; encode so the leading + survives. */
const phonePath = (/** @type {string} */ phone) => `${BASE}/inbox/conversations/${encodeURIComponent(phone)}`;

const adminWhatsAppService = {
  /** @returns {Promise<WhatsAppStatus>} */
  async getStatus() {
    return api.get(`${BASE}/status`);
  },

  // ---- templates

  /**
   * @param {{status?: string}} [params] e.g. {status: 'approved'}
   * @returns {Promise<WhatsAppTemplate[]>}
   */
  async listTemplates({ status } = {}) {
    return api.get(`${BASE}/templates${query({ status })}`);
  },

  /**
   * Pulls every template from WhatsApp. 503 when not configured, 502 when WhatsApp is unreachable.
   * @returns {Promise<TemplateSyncResult>}
   */
  async syncTemplates() {
    return api.post(`${BASE}/templates/sync`);
  },

  // ---- campaigns

  /**
   * Who a campaign would reach, without creating anything.
   * @param {AudienceInput} audience
   * @returns {Promise<AudiencePreview>}
   */
  async previewAudience(audience) {
    return api.post(`${BASE}/audience/preview`, audience);
  },

  /**
   * Snapshots the audience into a draft campaign (nothing is sent).
   * @param {CampaignInput} data
   * @returns {Promise<Campaign>}
   */
  async createCampaign(data) {
    return api.post(`${BASE}/campaigns`, data);
  },

  /** @returns {Promise<Campaign[]>} newest first, each with per-status `counts` */
  async listCampaigns() {
    return api.get(`${BASE}/campaigns`);
  },

  /**
   * One campaign with a page of its recipients.
   * @param {string} id
   * @param {{status?: string, page?: number, limit?: number}} [params] status filters recipients; page is 1-based (limit 1-200, default 50)
   * @returns {Promise<CampaignDetail>}
   */
  async getCampaign(id, { status, page, limit } = {}) {
    return api.get(`${BASE}/campaigns/${id}${query({ status, page, limit })}`);
  },

  /**
   * Sends ONE real message to this number; a campaign can only start after a successful test.
   * @param {string} id
   * @param {{phone: string, country?: string | null}} data
   * @returns {Promise<{ok: boolean, test_sent_at: string}>}
   */
  async testSend(id, data) {
    return api.post(`${BASE}/campaigns/${id}/test`, data);
  },

  /**
   * Starts a tested draft campaign. 409 when it is not a tested draft or sending is switched off.
   * @param {string} id
   * @returns {Promise<{status: string, queued: number, started_at: string}>}
   */
  async sendCampaign(id) {
    return api.post(`${BASE}/campaigns/${id}/send`);
  },

  /**
   * Schedules a tested draft campaign for a future UTC instant. 400 if that instant is not in the
   * future, 409 if it is not a tested draft or has no sendable recipients.
   * @param {string} id
   * @param {string} scheduledAtIso ISO 8601 instant in UTC
   * @returns {Promise<{status: string, queued: number, scheduled_at: string}>}
   */
  async scheduleCampaign(id, scheduledAtIso) {
    return api.post(`${BASE}/campaigns/${id}/schedule`, { scheduled_at: scheduledAtIso });
  },

  /**
   * @param {string} id
   * @returns {Promise<{status: string}>} 409 when it has already finished or been cancelled
   */
  async cancelCampaign(id) {
    return api.post(`${BASE}/campaigns/${id}/cancel`);
  },

  /**
   * Re-queues failed rows. By default only definite rejections; includeUncertain also re-queues rows
   * that may already have been delivered (can send duplicates). 409 when nothing is eligible.
   * @param {string} id
   * @param {{includeUncertain?: boolean}} [options]
   * @returns {Promise<RetryResult>}
   */
  async retryFailed(id, { includeUncertain = false } = {}) {
    return api.post(`${BASE}/campaigns/${id}/retry-failed`, { include_uncertain: includeUncertain });
  },

  /**
   * Clears an auto-pause (the dispatcher's consecutive-failure breaker) so the queued rows it left
   * untouched resume sending. 409 when the campaign isn't paused.
   * @param {string} id
   * @returns {Promise<{status: string}>}
   */
  async resumeCampaign(id) {
    return api.post(`${BASE}/campaigns/${id}/resume`);
  },

  // ---- inbox

  /**
   * Cheap unread counters for the admin-wide new-message alerts.
   * @returns {Promise<{unread_total: number, unread_conversations: number, latest: {id?: string, phone: string, name?: string | null, preview?: string | null, at?: string | null} | null}>}
   */
  async getUnreadSummary() {
    return api.get(`${BASE}/inbox/unread-summary`);
  },

  /**
   * @param {{limit?: number, offset?: number}} [params] limit 1-200 (default 50)
   * @returns {Promise<Conversation[]>} most recently active first
   */
  async listConversations({ limit, offset } = {}) {
    return api.get(`${BASE}/inbox/conversations${query({ limit, offset })}`);
  },

  /**
   * @param {string} phone E.164, e.g. +919876543210
   * @param {{limit?: number, before?: string}} [params] before is an ISO time; older messages only
   * @returns {Promise<ConversationDetail>}
   */
  async getConversation(phone, { limit, before } = {}) {
    return api.get(`${phonePath(phone)}${query({ limit, before })}`);
  },

  /**
   * Fetches one inbound message's media on demand (lazy - nothing is downloaded until the admin
   * opens it). Cached on the backend after the first call, so calling this again for the same
   * message is free. 404 if the message has no media; 502 if Meta's media id has expired or the
   * fetch otherwise failed - safe to let the admin retry.
   * @param {string} messageId
   * @returns {Promise<{url: string, mime_type: string}>}
   */
  async fetchInboundMedia(messageId) {
    return api.get(`${BASE}/inbox/inbound/${messageId}/media`);
  },

  /**
   * @param {string} phone E.164
   * @returns {Promise<{marked: number}>}
   */
  async markRead(phone) {
    return api.post(`${phonePath(phone)}/read`);
  },

  /**
   * Queues a free-form reply. 409 (err.code send_disabled, window_closed or suppressed) when refused.
   * @param {string} phone E.164
   * @param {string} text
   * @param {string} [replyToMessageId] quotes that message (ours or the customer's) - 422 (err.code invalid_target) if it isn't in this conversation
   * @returns {Promise<ThreadMessage>}
   */
  async reply(phone, text, replyToMessageId) {
    return api.post(`${phonePath(phone)}/reply`, { text, reply_to_message_id: replyToMessageId });
  },

  /**
   * Queues a media reply from an already uploaded file. Same 409 codes as reply(), plus 422
   * (err.code invalid_media, caption_not_allowed, text_too_long or invalid_target).
   * @param {string} phone E.164
   * @param {{url: string, mime_type: string, caption?: string, filename?: string, reply_to_message_id?: string}} body
   * @returns {Promise<ThreadMessage>}
   */
  async replyMedia(phone, body) {
    return api.post(`${phonePath(phone)}/reply-media`, body);
  },

  /**
   * Reacts to (or, with emoji `''`, removes a reaction from) a specific message. Same 409 codes
   * as reply(); 422 (err.code invalid_target) if the message isn't in this conversation.
   * @param {string} phone E.164
   * @param {string} targetMessageId
   * @param {string} emoji
   * @returns {Promise<ThreadMessage>}
   */
  async react(phone, targetMessageId, emoji) {
    return api.post(`${phonePath(phone)}/react`, { target_message_id: targetMessageId, emoji });
  },

  /**
   * Edits a reply's text before it's been sent - WhatsApp has no way to change a message once
   * Meta has accepted it, so this only works while the message is still queued. 409 (err.code
   * not_editable) once it's no longer eligible.
   * @param {string} phone E.164
   * @param {string} messageId
   * @param {string} text
   * @returns {Promise<{ok: boolean, text: string}>}
   */
  async editMessage(phone, messageId, text) {
    return api.patch(`${phonePath(phone)}/messages/${messageId}`, { text });
  },

  /**
   * Deletes a message from our own inbox view only - WhatsApp gives a business no way to recall
   * or unsend something already on the customer's phone. A still-queued message is cancelled
   * outright; anything else (already sent, or any inbound message) becomes a tombstone.
   * @param {string} phone E.164
   * @param {string} messageId
   * @returns {Promise<{ok: boolean, removed: boolean}>}
   */
  async deleteMessage(phone, messageId) {
    return api.del(`${phonePath(phone)}/messages/${messageId}`);
  },

  /**
   * Sends a buttons/list/link-button interactive message. 422 (err.code invalid_buttons,
   * invalid_list, invalid_url or invalid_kind) when Meta's own shape limits are violated.
   * @param {string} phone E.164
   * @param {{kind: 'buttons'|'list'|'cta_url', body: string, footer?: string, buttons?: {id: string, title: string}[], button_text?: string, sections?: {title: string, rows: {id: string, title: string, description?: string}[]}[], url?: string}} payload
   * @returns {Promise<ThreadMessage>}
   */
  async sendInteractive(phone, payload) {
    return api.post(`${phonePath(phone)}/send-interactive`, payload);
  },

  /**
   * Sends the connected Meta catalog for the customer to browse. 409 (err.code
   * catalog_not_configured) if WHATSAPP_CATALOG_ID isn't set on the server.
   * @param {string} phone E.164
   * @param {string} body
   * @returns {Promise<ThreadMessage>}
   */
  async sendCatalog(phone, body) {
    return api.post(`${phonePath(phone)}/send-catalog`, { body });
  },

  /**
   * Queues an approved template to any number, the way to open a chat outside the 24 hour window.
   * 409 (err.code send_disabled, suppressed or window_open) or 422 (err.code template_unavailable,
   * invalid_params or unsupported_template) when refused.
   * @param {string} phone E.164
   * @param {{template_id: string, body_params: string[], display_name?: string}} body
   * @returns {Promise<ThreadMessage>}
   */
  async sendTemplate(phone, body) {
    return api.post(`${phonePath(phone)}/send-template`, body);
  },

  /**
   * Uploads an attachment to storage (the server converts voice-note webm to ogg and compresses big
   * videos). Send the message with the RESPONSE's `format` and `size`, not the picked file's.
   * @param {File} file
   * @returns {Promise<{url: string, format: string, size: number, resource_type?: string}>}
   */
  async uploadMedia(file) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('upload_type', 'whatsapp-media');
    return api.upload('/api/upload/whatsapp-media', formData);
  },

  // ---- triggers

  /** @returns {Promise<Trigger[]>} all four triggers, disabled ones included */
  async listTriggers() {
    return api.get(`${BASE}/triggers`);
  },

  /**
   * Only the fields sent change. Enabling needs an approved template with every placeholder mapped.
   * @param {string} key
   * @param {{template_id?: string | null, variable_map?: Record<string, string>, enabled?: boolean}} patch
   * @returns {Promise<Trigger>}
   */
  async updateTrigger(key, patch) {
    return api.patch(`${BASE}/triggers/${encodeURIComponent(key)}`, patch);
  },

  // ---- record-page template actions

  /**
   * The templates a record page offers in its dropdown, with whether each is approved yet.
   * @param {'orders' | 'customers' | 'leads'} scope
   * @returns {Promise<Array<{kind: string, label: string, template_name: string, approved: boolean, needs_sender: boolean, preview: string}>>}
   */
  async listRecordTemplates(scope) {
    return api.get(`${BASE}/inbox/${scope}/templates`);
  },

  /**
   * Send a record's approved template, filled from the record itself. Template-only, so it works
   * inside or outside the 24-hour reply window.
   * @param {'orders' | 'customers' | 'leads'} scope
   * @param {string} id
   * @param {string} kind e.g. ready_checkin, balance_payment_link, intro, event_greeting
   * @param {string} [senderName] who is writing; required by the templates that introduce a person
   * @returns {Promise<{id: string, text: string, template_name: string}>}
   */
  async sendRecordTemplate(scope, id, kind, senderName) {
    return api.post(`${BASE}/inbox/${scope}/${encodeURIComponent(id)}/send-template`, {
      kind,
      sender_name: senderName,
    });
  },
};

export default adminWhatsAppService;
